import { useState, useEffect } from 'react';
import {
    Modal,
    Typography,
    Box,
    Button,
    Switch,
    FormControlLabel,
    Divider,
    Snackbar,
    Alert
} from "@mui/material";
import ModalBox from "../themes/overrides/ModalBox";
import TextFieldInput from '../themes/overrides/TextFieldInput';
import { useDispatch, useSelector } from "react-redux";
import { putWorkspace } from '../actions/workspaceAction';
import { flushCdnCache } from '../api/api';
import LoadingOverlay from './LoadingOverlay';
import PropTypes from 'prop-types';

const ManageCdnCacheModal = ({ open, onClose, selectedType, onSuccess }) => {
    const dispatch = useDispatch();
    const [cdnEnabled, setCdnEnabled] = useState(true);
    const [pathsText, setPathsText] = useState('');
    const [loading, setLoading] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const currentWorkspace = useSelector((state) => state.workspace.workspaceInfo);

    useEffect(() => {
        if (selectedType) {
            // Default to enabled when the flag has never been set.
            setCdnEnabled(currentWorkspace?.data?.invalidateCdnCache ?? true);
        }
    }, [currentWorkspace, selectedType]);

    const showSnackbar = (message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setOpenSnackbar(true);
    };

    const handleToggle = async (event) => {
        const checked = event.target.checked;
        setCdnEnabled(checked);
        setLoading(true);
        try {
            await dispatch(putWorkspace(selectedType._id, checked, 'invalidateCdnCache'));
            onSuccess?.();
            showSnackbar(
                `Automatic CDN invalidation ${checked ? 'enabled' : 'disabled'}`,
                'success'
            );
        } catch (error) {
            setCdnEnabled(!checked);
            showSnackbar('Failed to update CDN setting', 'error');
            console.error('Error updating CDN setting:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFlush = async () => {
        const paths = pathsText
            .split('\n')
            .map((p) => p.trim())
            .filter((p) => p.length > 0);

        if (paths.length === 0) {
            showSnackbar('Enter at least one path to flush', 'error');
            return;
        }

        setLoading(true);
        try {
            const res = await flushCdnCache(selectedType._id, paths);
            const { status, message, count } = res.data || {};
            if (status === 'success') {
                showSnackbar(`Cache flush requested for ${count} path(s)`, 'success');
                setPathsText('');
            } else if (status === 'skipped') {
                showSnackbar(message || 'CDN flush only runs in production', 'warning');
            } else {
                showSnackbar(message || 'Failed to flush cache', 'error');
            }
        } catch (error) {
            showSnackbar('Failed to flush cache', 'error');
            console.error('Error flushing CDN cache:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalBox sx={{ width: '700px', maxWidth: '95%' }}>
                <Typography variant="h2" sx={{ mb: 3 }}>Manage CDN Cache</Typography>

                <FormControlLabel
                    control={<Switch checked={cdnEnabled} onChange={handleToggle} />}
                    label="Automatically clear CDN cache when entries change"
                    componentsProps={{ typography: { variant: 'body1' } }}
                />
                <Typography sx={{ mt: 1, mb: 3, color: '#ACACAC' }} variant="body2">
                    When off, editing entries will not trigger a CDN invalidation. Manual flushes below still work.
                </Typography>

                <Divider sx={{ mb: 3 }} />

                <Typography variant="h6" sx={{ mb: 1 }}>Manually flush cache</Typography>
                <Typography sx={{ mb: 2, color: '#ACACAC' }} variant="body2">
                    One path per line. Paths must start with <code>/</code> (e.g. <code>{'/api/entry/workspace/<id>/content/<entryId>/*'}</code> or <code>{'/*'}</code>). Runs in production only.
                </Typography>

                <TextFieldInput
                    label="Paths"
                    placeholder={'/api/entry/...\n/*'}
                    multiline
                    minRows={4}
                    fullWidth
                    value={pathsText}
                    onChange={(e) => setPathsText(e.target.value)}
                    sx={{ mb: 3 }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        onClick={handleFlush}
                        disabled={loading || pathsText.trim().length === 0}
                    >
                        Flush Cache
                    </Button>
                </Box>

                <Snackbar
                    open={openSnackbar}
                    autoHideDuration={3000}
                    onClose={() => setOpenSnackbar(false)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={() => setOpenSnackbar(false)}
                        severity={snackbarSeverity}
                        sx={{ width: '100%' }}
                    >{snackbarMessage}</Alert>
                </Snackbar>
                {loading && <LoadingOverlay />}
            </ModalBox>
        </Modal>
    );
};

ManageCdnCacheModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    selectedType: PropTypes.any,
    onSuccess: PropTypes.func
};

export default ManageCdnCacheModal;
